/**
 * ResearchDashboardPage (86d3cefah S6; reshaped to one screen by 86d3qa4fq).
 *
 * A research workbench, not a set of tabs: one search box drives BOTH the clinic knowledge base
 * and MedQuery literature, and both results are on screen together — a practitioner should see
 * whether our own monographs cover the query they just ran against PubMed without switching
 * views. Three viewport-height columns, each scrolling independently, no page scrollbar:
 *
 *   left 240   Refine (facets + year) over Recent searches
 *   centre     Literature — PubMed-style citations, server-side paging
 *   right 340  Knowledge base — monographs, documents, links
 *
 * Literature specifics (unchanged from the tabbed version):
 *   - Facets are requested in the SAME search call (never one request per facet field — the
 *     gateway's per-key rate budget matters), and buckets are capped ~100/field upstream, so
 *     the panel shows the top values for the result set, not an exhaustive enumeration.
 *   - Offset paging only reaches the first 1000 results; `max_offset_page` bounds the pager and
 *     an inline note explains the cap when `total` exceeds the reachable window.
 *   - Year filtering uses `pub_year` (integer, always populated), never `pub_date` (null on ~2%).
 *   - Every control change resets to page 1 and is batched into ONE request.
 *
 * A literature row opens LiteratureArticleDrawer (which fetches the full PubMed record); a
 * knowledge row opens ResearchDetailDrawer. Search history is recorded HERE as one `all` row —
 * see the recording effect below. The literature-unavailable envelope renders an inline notice,
 * never a blank pane. All colour via --mg-* tokens; drawers via DetailDrawer.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Typography, Select, Space, Tag, Grid } from 'antd';

import Layout from '../../components/Layout';
import { PageContainer, PageToolbar } from '../../components/shared';
import { ENV } from '../../config/runtimeEnv';
import { useNotifications } from '../../context/NotificationContext';
import {
	useKnowledgeSearch,
	useLiteratureSearch,
	useSaveResearchSearch,
} from '../../hooks/useResearch';
import { useCreateXoeConversation } from '../../hooks/xoe/useXoe';
import { openXoeConversation } from '../../utils/xoe/openXoeConversation';
import type {
	FilterField,
	KnowledgeGroup,
	KnowledgeResult,
	LiteratureFilter,
	LiteratureResult,
	LiteratureSearchParams,
	LiteratureSort,
	ResearchRecord,
	ResearchSearch,
} from '../../types/research/ResearchTypes';
import RecentSearchesRail from './RecentSearchesRail';
import ResearchDetailDrawer from './ResearchDetailDrawer';
import LiteratureArticleDrawer from './LiteratureArticleDrawer';
import RefinePanel from './RefinePanel';
import LiteratureResultsPane from './LiteratureResultsPane';
import KnowledgeResultsPane from './KnowledgeResultsPane';
import { useFillHeight } from './useFillHeight';

const { Text } = Typography;

const DEBOUNCE_MS = 350;
const MIN_QUERY_LEN = 2;

const LEFT_RAIL_W = 240;
const RIGHT_RAIL_W = 340;

// ── Literature controls ─────────────────────────────────────────────────────────

/** Requested with every search — one call, never one request per facet field. */
const LITERATURE_FACETS: FilterField[] = [
	'pub_types',
	'journal_facet',
	'mesh_major',
	'language',
	'author_facet',
];

/** `pmid` is a valid API sort but is not user-meaningful — deliberately not offered. */
const SORT_OPTIONS: { label: string; value: LiteratureSort }[] = [
	{ label: 'Relevance', value: 'relevance' },
	{ label: 'Newest first', value: 'newest' },
	{ label: 'Oldest first', value: 'oldest' },
];

const SIZE_OPTIONS = [10, 20, 50, 100].map((n) => ({ label: `${n} / page`, value: n }));

const DEFAULT_SIZE = 20;

const FIELD_LABEL: Partial<Record<FilterField, string>> = {
	pub_types: 'Publication type',
	journal_facet: 'Journal',
	mesh_major: 'MeSH major topic',
	language: 'Language',
	author_facet: 'Author',
	pub_year: 'Year',
};

const fieldLabel = (f: FilterField): string => FIELD_LABEL[f] ?? f;

/** A stable identity for a filter clause (term or range). */
const filterKey = (f: LiteratureFilter): string =>
	f.value != null ? `${f.field}:${f.value}` : `${f.field}:${f.from ?? ''}-${f.to ?? ''}`;

/** Chip copy — "Journal: Nature" / "Year: 2019–2024" / "Year: from 2019". */
const filterLabel = (f: LiteratureFilter): string => {
	if (f.value != null) return `${fieldLabel(f.field)}: ${f.value}`;
	if (f.from && f.to) return `${fieldLabel(f.field)}: ${f.from}–${f.to}`;
	if (f.from) return `${fieldLabel(f.field)}: from ${f.from}`;
	return `${fieldLabel(f.field)}: to ${f.to}`;
};

// ── Row mappers (attach group context so the drawer can render type-specific detail) ──

const toKnowledgeRecord = (r: KnowledgeResult, group: KnowledgeGroup): ResearchRecord => ({
	...r,
	source_type: group.source_type,
	source_category: group.source_category,
});

const toLiteratureRecord = (r: LiteratureResult): ResearchRecord => ({
	...r,
	meta: r.meta as Record<string, unknown>,
	source_type: 'literature',
	source_category: 'medical_literature',
});

const metaStr = (meta: Record<string, unknown>, key: string): string | null => {
	const v = meta[key];
	return typeof v === 'string' && v.length > 0 ? v : v != null && v !== '' ? String(v) : null;
};

// ── Page ────────────────────────────────────────────────────────────────────────

const ResearchDashboardPage: React.FC = () => {
	const { showError } = useNotifications();
	const xoeEnabled = ENV.VITE_XOE_ENABLED === 'true';
	const screens = Grid.useBreakpoint();
	/** Below lg the three columns stack and the page scrolls normally (§21). */
	const isWide = !!screens.lg;

	const [searchTerm, setSearchTerm] = useState('');
	const [query, setQuery] = useState('');

	const [selected, setSelected] = useState<ResearchRecord | null>(null);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [discussing, setDiscussing] = useState(false);

	// ── Literature controls ───────────────────────────────────────────────────
	const [litSort, setLitSort] = useState<LiteratureSort>('relevance');
	const [litPage, setLitPage] = useState(1);
	const [litSize, setLitSize] = useState(DEFAULT_SIZE);
	/** Term filters added by clicking facet buckets. */
	const [termFilters, setTermFilters] = useState<LiteratureFilter[]>([]);
	/** Committed pub_year range (the applied filter), distinct from the input values. */
	const [yearRange, setYearRange] = useState<{ from?: string; to?: string } | null>(null);
	const [yearFromInput, setYearFromInput] = useState<number | null>(null);
	const [yearToInput, setYearToInput] = useState<number | null>(null);

	// ── Debounce the raw search term into the committed query ─────────────────
	// Both setStates land in one React batch, so a new query issues ONE round at page 1.
	useEffect(() => {
		const t = setTimeout(() => {
			const trimmed = searchTerm.trim();
			setQuery(trimmed.length >= MIN_QUERY_LEN ? trimmed : '');
			setLitPage(1);
		}, DEBOUNCE_MS);
		return () => clearTimeout(t);
	}, [searchTerm]);

	// ── Queries ───────────────────────────────────────────────────────────────
	// Both always run: one screen means both panes answer every query. `record: false` on each —
	// the single history row is written by the recording effect below.
	const knowledge = useKnowledgeSearch(query, [], true, false);

	// Year is a RANGE clause on pub_year (integer, always populated) — never pub_date.
	const yearFilter = useMemo<LiteratureFilter | null>(() => {
		if (!yearRange || (!yearRange.from && !yearRange.to)) return null;
		return {
			field: 'pub_year',
			...(yearRange.from ? { from: yearRange.from } : {}),
			...(yearRange.to ? { to: yearRange.to } : {}),
		};
	}, [yearRange]);

	const activeFilters = useMemo<LiteratureFilter[]>(
		() => (yearFilter ? [...termFilters, yearFilter] : termFilters),
		[termFilters, yearFilter],
	);

	const literatureParams = useMemo<LiteratureSearchParams>(
		() => ({
			q: query,
			sort: litSort,
			page: litPage,
			size: litSize,
			...(activeFilters.length > 0 ? { filters: activeFilters } : {}),
			facets: LITERATURE_FACETS,
			record: false,
		}),
		[query, litSort, litPage, litSize, activeFilters],
	);

	const literature = useLiteratureSearch(literatureParams);

	// ── Literature control handlers (each resets to page 1, batched into one request) ──
	const changeSort = (v: LiteratureSort) => {
		setLitSort(v);
		setLitPage(1);
	};

	const changeSize = (v: number) => {
		setLitSize(v);
		setLitPage(1);
	};

	/** Clicking a facet bucket ADDS a term filter and re-queries from page 1. */
	const addTermFilter = (field: FilterField, value: string) => {
		setTermFilters((prev) =>
			prev.some((f) => f.field === field && f.value === value) ? prev : [...prev, { field, value }],
		);
		setLitPage(1);
	};

	const removeFilter = (target: LiteratureFilter) => {
		if (target.field === 'pub_year' && target.value == null) {
			setYearRange(null);
			setYearFromInput(null);
			setYearToInput(null);
		} else {
			setTermFilters((prev) => prev.filter((f) => filterKey(f) !== filterKey(target)));
		}
		setLitPage(1);
	};

	const applyYearRange = () => {
		const from = yearFromInput != null ? String(yearFromInput) : undefined;
		const to = yearToInput != null ? String(yearToInput) : undefined;
		setYearRange(from || to ? { from, to } : null);
		setLitPage(1);
	};

	/** Back to the default literature view (used on reset + history re-run). */
	const resetLiteratureControls = () => {
		setLitSort('relevance');
		setLitPage(1);
		setLitSize(DEFAULT_SIZE);
		setTermFilters([]);
		setYearRange(null);
		setYearFromInput(null);
		setYearToInput(null);
	};

	// ── Search history ────────────────────────────────────────────────────────
	// This dashboard searches BOTH endpoints for one query, and neither endpoint can see the
	// other, so letting them auto-record would write two rows per search. Both are called with
	// `record: false` and the single `all` row is written here — this is the only place that
	// knows the two searches were one search. Ref-guarded to once per committed query; the
	// mutation's onSuccess invalidates ['research','history'], which refreshes the rail.
	const saveSearch = useSaveResearchSearch();
	const recordedQueryRef = useRef<string | null>(null);

	const knowledgeSettled = !knowledge.isLoading && !knowledge.isFetching;
	const literatureSettled = !literature.isLoading && !literature.isFetching;
	const bothFailed = knowledge.isError && literature.isError;

	useEffect(() => {
		if (!query) {
			recordedQueryRef.current = null;
			return;
		}
		if (recordedQueryRef.current === query) return;
		if (!knowledgeSettled || !literatureSettled) return;
		// Both sides failing is not a search worth remembering.
		if (bothFailed) return;

		recordedQueryRef.current = query;

		const knowledgeTotal = knowledge.data?.total ?? 0;
		const literatureTotal = literature.data?.available ? literature.data.total : 0;

		saveSearch.mutate({
			query,
			source: 'all',
			origin: 'dashboard',
			result_count: knowledgeTotal + literatureTotal,
			filters: activeFilters.length > 0 ? { filters: activeFilters } : null,
		});
		// Fires once per committed query, once both panes have settled.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query, knowledgeSettled, literatureSettled, bothFailed]);

	// ── Xoe handoff (S9 / P8.4) ────────────────────────────────────────────────
	// Research creates and titles the conversation (from `record.title` — handleSend's own
	// create branch would title it from the question text instead, filling the conversation
	// list with truncated prompts) and hands the SEED TEXT to the widget via
	// openXoeConversation(). The widget's own XoeChat runs the turn through its
	// existing dual-path transport logic, so this page never sends the message itself and
	// never needs to know which host (healthcare vs xoe-agent) is live for the clinic.
	const createConversation = useCreateXoeConversation();

	const handleDiscuss = async (record: ResearchRecord) => {
		if (discussing) return;

		const isPaperRecord = record.source_category === 'medical_literature';
		const title = record.title || 'this item';
		const pmid = metaStr(record.meta, 'pmid');
		const message = isPaperRecord
			? `Summarise this paper: ${title}${pmid ? ` (PMID ${pmid})` : ''}`
			: `What does our knowledge base say about ${title}?`;
		try {
			setDiscussing(true);
			const conv = await createConversation.mutateAsync({ title: title.slice(0, 60) });
			openXoeConversation(conv.uuid, message);
			setDrawerOpen(false);
		} catch (e) {
			showError('Could not start a Xoe conversation', 'Please try again.');
		} finally {
			setDiscussing(false);
		}
	};

	// ── Row / history handlers ────────────────────────────────────────────────
	const openLiterature = (r: LiteratureResult) => {
		setSelected(toLiteratureRecord(r));
		setDrawerOpen(true);
	};

	const openKnowledge = (r: KnowledgeResult, group: KnowledgeGroup) => {
		setSelected(toKnowledgeRecord(r, group));
		setDrawerOpen(true);
	};

	const handleSelectHistory = (search: ResearchSearch) => {
		setSearchTerm(search.query);
		setQuery(search.query.trim());
		// Re-running a stored query starts from that query's default view.
		resetLiteratureControls();
	};

	const resetSearch = () => {
		setSearchTerm('');
		setQuery('');
		resetLiteratureControls();
	};

	// ── Layout ────────────────────────────────────────────────────────────────
	const columnsRef = useRef<HTMLDivElement>(null);
	const columnsHeight = useFillHeight(columnsRef, isWide);

	const isPaper = selected?.source_category === 'medical_literature';
	const maxOffsetPage = literature.data?.max_offset_page ?? 1;

	const facetGroups = LITERATURE_FACETS.map((field) => ({
		field,
		buckets: literature.data?.facets?.[field] ?? [],
	})).filter((g) => g.buckets.length > 0);

	return (
		<Layout>
			<PageContainer data-testid="research-dashboard">
				<PageToolbar
					filters={[
						{
							type: 'search',
							key: 'research-search',
							placeholder: 'Search knowledge base and literature…',
							value: searchTerm,
							onChange: setSearchTerm,
							width: 320,
						},
						{
							type: 'custom',
							key: 'research-sort',
							render: (
								<Select<LiteratureSort>
									value={litSort}
									onChange={changeSort}
									options={SORT_OPTIONS}
									style={{ width: 150 }}
									aria-label="Sort literature"
									data-testid="research-literature-sort"
								/>
							),
						},
						{
							type: 'custom',
							key: 'research-size',
							render: (
								<Select<number>
									value={litSize}
									onChange={changeSize}
									options={SIZE_OPTIONS}
									style={{ width: 110 }}
									aria-label="Results per page"
									data-testid="research-literature-size"
								/>
							),
						},
					]}
					onReset={resetSearch}
				/>

				{activeFilters.length > 0 && (
					<Space size={4} wrap data-testid="research-filter-chips">
						<Text className="mg-label">Filters</Text>
						{activeFilters.map((f) => (
							<Tag
								key={filterKey(f)}
								closable
								onClose={(e) => {
									e.preventDefault();
									removeFilter(f);
								}}
								style={{ fontSize: 'var(--mg-fs-xs)' }}
								data-testid={`research-filter-chip-${filterKey(f)}`}
							>
								{filterLabel(f)}
							</Tag>
						))}
					</Space>
				)}

				<div
					ref={columnsRef}
					style={{
						display: 'flex',
						gap: 16,
						alignItems: 'stretch',
						height: columnsHeight,
						minHeight: 0,
						...(isWide ? {} : { flexDirection: 'column' }),
					}}
				>
					{/* Left rail — Refine over Recent searches. Every minmax(0,…) here is
					    load-bearing: grid items default to min-width/min-height auto, so without
					    them the cards size to min-content and spill out of the rail — over the
					    literature column horizontally, and past the viewport vertically. */}
					<div
						style={{
							width: isWide ? LEFT_RAIL_W : undefined,
							flexShrink: 0,
							display: 'grid',
							gridTemplateColumns: 'minmax(0, 1fr)',
							gridTemplateRows: query ? 'minmax(0, 3fr) minmax(0, 2fr)' : 'minmax(0, 1fr)',
							gap: 16,
							minWidth: 0,
							minHeight: 0,
						}}
					>
						<RefinePanel
							visible={!!query}
							facetGroups={facetGroups}
							fieldLabel={fieldLabel}
							onAddTermFilter={addTermFilter}
							yearFrom={yearFromInput}
							yearTo={yearToInput}
							onYearFromChange={setYearFromInput}
							onYearToChange={setYearToInput}
							onApplyYear={applyYearRange}
						/>
						<RecentSearchesRail onSelect={handleSelectHistory} />
					</div>

					{/* Centre — literature */}
					<div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
						<LiteratureResultsPane
							query={query}
							data={literature.data}
							isLoading={literature.isLoading}
							isError={literature.isError}
							page={litPage}
							size={litSize}
							maxOffsetPage={maxOffsetPage}
							onPageChange={setLitPage}
							onOpen={openLiterature}
							selectedId={isPaper ? selected?.id : null}
						/>
					</div>

					{/* Right rail — knowledge base */}
					<div style={{ width: isWide ? RIGHT_RAIL_W : undefined, flexShrink: 0, minWidth: 0, minHeight: 0 }}>
						<KnowledgeResultsPane
							query={query}
							data={knowledge.data}
							isLoading={knowledge.isLoading}
							isError={knowledge.isError}
							onOpen={openKnowledge}
							selectedId={!isPaper ? selected?.id : null}
						/>
					</div>
				</div>

				{/* A paper and a knowledge record are different things — a paper needs its full
				    PubMed record fetched, which only LiteratureArticleDrawer does. */}
				{selected && isPaper ? (
					<LiteratureArticleDrawer
						open={drawerOpen}
						onClose={() => setDrawerOpen(false)}
						record={selected}
						xoeEnabled={xoeEnabled}
						onDiscuss={handleDiscuss}
						discussing={discussing}
					/>
				) : (
					<ResearchDetailDrawer
						open={drawerOpen}
						onClose={() => setDrawerOpen(false)}
						record={selected}
						xoeEnabled={xoeEnabled}
						onDiscuss={handleDiscuss}
						discussing={discussing}
					/>
				)}
			</PageContainer>
		</Layout>
	);
};

export default ResearchDashboardPage;
